import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { localeDirection, localeTag, translate, type StringKey, type StringParams } from '@/i18n/strings';

/**
 * Access to the localization resources from a component.
 *
 * This is a context rather than a direct import of `translate` so that swapping
 * the resource map later — a second language, or a per-account language
 * preference — re-renders the tree instead of leaving stale strings behind in
 * memoized subtrees.
 */
interface LocalizationValue {
  t: (key: StringKey, params?: StringParams) => string;
  locale: string;
  direction: 'ltr' | 'rtl';
  formatTime: (epochMs: number) => string;
  formatDayAndTime: (epochMs: number) => string;
  formatRelativeDay: (epochMs: number) => string;
}

const LocalizationContext = createContext<LocalizationValue | null>(null);

function startOfLocalDay(epochMs: number): number {
  const date = new Date(epochMs);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

export function LocalizationProvider({ children }: { children: ReactNode }) {
  const value = useMemo<LocalizationValue>(() => {
    // Section 14 fixes local timezone for display and server creation sequence
    // for ordering. These formatters therefore deliberately use the device
    // timezone and are never used to sort anything.
    const timeFormat = new Intl.DateTimeFormat(localeTag, { hour: 'numeric', minute: '2-digit' });
    const dayFormat = new Intl.DateTimeFormat(localeTag, { month: 'short', day: 'numeric' });
    const dayWithYearFormat = new Intl.DateTimeFormat(localeTag, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const weekdayFormat = new Intl.DateTimeFormat(localeTag, { weekday: 'short' });

    return {
      t: translate,
      locale: localeTag,
      direction: localeDirection,
      formatTime: (epochMs) => timeFormat.format(epochMs),
      formatDayAndTime: (epochMs) => `${dayFormat.format(epochMs)} ${timeFormat.format(epochMs)}`,
      formatRelativeDay: (epochMs) => {
        const today = startOfLocalDay(Date.now());
        const day = startOfLocalDay(epochMs);
        const dayMs = 24 * 60 * 60 * 1000;
        if (day === today) {
          return timeFormat.format(epochMs);
        }
        if (today - day < 7 * dayMs) {
          return weekdayFormat.format(epochMs);
        }
        if (new Date(epochMs).getFullYear() === new Date().getFullYear()) {
          return dayFormat.format(epochMs);
        }
        return dayWithYearFormat.format(epochMs);
      },
    };
  }, []);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
}

export function useStrings(): LocalizationValue {
  const value = useContext(LocalizationContext);
  if (!value) {
    throw new Error('useStrings must be used inside LocalizationProvider');
  }
  return value;
}
